/**
 * THE PAYROLL TAX RETURN (docs/skills/payroll-tax-return.md) — what the current version says, set the way a
 * professional's file is set: the goal and the work programme with where each procedure stands; the return (the lead
 * schedule); what is left for the accountant to decide; then the supporting schedules, every line ticked to its row.
 *
 * EVERY FIGURE AND EVERY WORD IS THE SERVER'S (`work.payrollTax`). This file arranges them and computes nothing: no
 * sum, no sign, no tie-out, no state.
 */
import { useState } from "react";
import { WorkActionResponse, type PayrollTaxView, type WorkFile } from "@leveri/contracts";
import { Button, Figure, Label, Notice, RuledList, Section, SectionHead, StateText, Statement, markerTone } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";

const SUMMARY = "cursor-pointer list-none text-[15px] font-semibold text-ink hover:underline";
const NUM = "text-right [font-variant-numeric:tabular-nums]";
const TH = "py-2 pr-4 text-left text-[11px] font-medium uppercase tracking-[0.09em] text-stone";
const TD = "border-t border-rule py-2.5 pr-4 align-top text-[14px] text-ink";

/** 1 — the answer, the goal, the work programme and the return. */
export function PayrollTaxLead({ payrollTax }: { payrollTax: PayrollTaxView }) {
  return (
    <Section id="figures">
      <Statement>{payrollTax.headline}</Statement>
      <p className="mt-3 text-[13px] text-stone">
        <StateText tone={markerTone(payrollTax.tone)}>{payrollTax.verdictLabel}</StateText>
        {" · "}{payrollTax.periodText} · {payrollTax.progressText}
      </p>

      <div className="mt-6" data-testid="payroll-goal">
        <Label>The goal</Label>
        <p className="mt-1.5 max-w-[720px] text-[15px] text-ink">{payrollTax.objective}</p>
      </div>

      <div className="mt-6" data-testid="payroll-programme">
        <Label>The work programme</Label>
        <ol className="mt-2">
          {payrollTax.programme.map((step, index) => (
            <li key={step.step} className="flex gap-4 border-t border-rule py-2.5">
              <span className="w-6 shrink-0 text-[13px] text-stone [font-variant-numeric:tabular-nums]">{index + 1}.</span>
              <div className="min-w-0 flex-1">
                <p className="text-[14px] text-ink">{step.step}</p>
                <p className="mt-0.5 text-[13px] text-stone">{step.note}</p>
              </div>
              <span className="shrink-0 text-[13px]"><StateText tone={markerTone(step.tone)}>{step.stateLabel}</StateText></span>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-6" data-testid="payroll-return">
        <Label>The return</Label>
        {payrollTax.returnLines.map((line, index) => (
          <div key={line.key}>
            <Figure first={index === 0} label={line.label} citation={null} value={line.text} total={line.total} />
            {line.note && <p className="mt-0.5 text-[12px] text-stone">{line.note}</p>}
          </div>
        ))}
        <p className="mt-3 text-[13px] text-stone">A draft for review. Leveri has not computed anyone's tax, and nothing has been filed with a tax authority or posted.</p>
      </div>
    </Section>
  );
}

/** 2 — what only the accountant can settle, the exceptions, and PREPARE AGAIN when the server offers it. */
export function PayrollTaxDecide({ work, payrollTax, onWork, onOutcome }: {
  work: WorkFile;
  payrollTax: PayrollTaxView;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const again = payrollTax.prepareAgain;
  if (payrollTax.reviewerItems.length === 0 && payrollTax.exceptions.length === 0 && !again.sentence) return null;

  async function prepareAgain() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${work.id}/prepare-again`, WorkActionResponse);
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section id="decide">
      {payrollTax.exceptions.length > 0 && (
        <div data-testid="payroll-exceptions">
          <SectionHead>Exceptions</SectionHead>
          <RuledList>
            {payrollTax.exceptions.map((e, index) => (
              <div key={`${index}-${e.text.slice(0, 24)}`} className="py-2.5">
                <p className="text-[14px] text-ink">{e.text}</p>
                <p className="mt-0.5 text-[12px]"><StateText tone={markerTone(e.tone)}>{e.audienceLabel}</StateText></p>
              </div>
            ))}
          </RuledList>
        </div>
      )}
      {payrollTax.reviewerItems.length > 0 && (
        <div className={payrollTax.exceptions.length > 0 ? "mt-6" : ""} data-testid="payroll-review-points">
          <SectionHead>Review points</SectionHead>
          <RuledList>
            {payrollTax.reviewerItems.map((item, index) => (
              <p key={`${index}-${item.slice(0, 24)}`} className="py-2.5 text-[14px] text-ink">{item}</p>
            ))}
          </RuledList>
          <p className="mt-2 text-[13px] text-stone">Decide each proposed payment in <a href="#review" className="underline">your review</a>; your decision is remembered for this client.</p>
        </div>
      )}
      {again.sentence && (
        <div className="mt-5">
          <p className="text-sm text-ink" data-testid="prepare-again-sentence">{again.sentence}</p>
          {again.available && (
            <div className="mt-3">
              <Button disabled={busy} onClick={() => void prepareAgain()} data-testid="prepare-again">
                {busy ? "Preparing again…" : "Prepare again with my decisions"}
              </Button>
            </div>
          )}
        </div>
      )}
      {failure && <Notice className="mt-3">{failure}</Notice>}
    </Section>
  );
}

/** 4 — the supporting schedules, one click away for whoever checks them; each line ticked to its row. */
export function PayrollTaxSchedules({ payrollTax }: { payrollTax: PayrollTaxView }) {
  const tick = (source: string) => (source ? <div className="mt-0.5 text-[12px] text-stone">✓ {source}</div> : null);
  return (
    <Section id="schedules">
      <SectionHead>Supporting schedules</SectionHead>

      <details className="mt-2" open data-testid="payroll-runs">
        <summary className={SUMMARY}>Pay runs</summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead><tr><th className={TH}>Pay run</th><th className={`${TH} ${NUM}`}>Employees</th><th className={`${TH} ${NUM}`}>Gross</th><th className={`${TH} ${NUM}`}>Tax withheld</th><th className={`${TH} ${NUM}`}>Other</th><th className={`${TH} ${NUM}`}>Net pay</th><th className={TH}>Net pay to the bank</th></tr></thead>
            <tbody>
              {payrollTax.payRuns.map((r) => (
                <tr key={r.label}>
                  <td className={TD}>{r.label}</td><td className={`${TD} ${NUM}`}>{r.employees}</td><td className={`${TD} ${NUM}`}>{r.grossText}</td>
                  <td className={`${TD} ${NUM}`}>{r.taxText}</td><td className={`${TD} ${NUM}`}>{r.otherText}</td><td className={`${TD} ${NUM}`}>{r.netText}</td>
                  <td className={TD}><StateText tone={markerTone(r.tone)}>{r.netLabel}</StateText></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="mt-4" data-testid="payroll-pays">
        <summary className={SUMMARY}>Each employee's pay ({payrollTax.pays.length})</summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead><tr><th className={TH}>Employee</th><th className={`${TH} ${NUM}`}>Gross</th><th className={`${TH} ${NUM}`}>Tax</th><th className={`${TH} ${NUM}`}>Other</th><th className={`${TH} ${NUM}`}>Net</th><th className={TH}>Adds up</th><th className={TH}>Paid</th></tr></thead>
            <tbody>
              {payrollTax.pays.map((p, index) => (
                <tr key={`${index}-${p.source}`}>
                  <td className={TD}>{p.employee}<div className="text-[12px] text-stone">{p.runLabel}</div>{tick(p.source)}</td>
                  <td className={`${TD} ${NUM}`}>{p.grossText}</td><td className={`${TD} ${NUM}`}>{p.taxText}</td><td className={`${TD} ${NUM}`}>{p.otherText}</td><td className={`${TD} ${NUM}`}>{p.netText}</td>
                  <td className={TD}>{p.arithmeticLabel}</td>
                  <td className={TD}><StateText tone={markerTone(p.tone)}>{p.settlementLabel}</StateText>{p.bank && tick(p.bank)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="mt-4" data-testid="payroll-net-pay">
        <summary className={SUMMARY}>Net pay to the bank</summary>
        <RuledList>
          {payrollTax.netPay.map((n) => (
            <div key={n.runLabel} className="py-2.5">
              <p className="text-[14px] text-ink">{n.runLabel}: {n.netText} · <StateText tone={markerTone(n.tone)}>{n.stateLabel}</StateText></p>
              {n.payments.map((p, index) => (
                <p key={`${index}-${p.source}`} className="mt-1 text-[13px] text-ink-soft">
                  {p.dateText} · “{p.words}” · {p.amountText} · {p.kindLabel}: {p.employees.join(", ")}
                  {tick(p.source)}
                </p>
              ))}
              {n.unmatched.map((u) => (
                <p key={u.source} className="mt-1 text-[13px] text-stone">Not found in the bank: {u.text}{tick(u.source)}</p>
              ))}
            </div>
          ))}
        </RuledList>
      </details>

      <details className="mt-4" open data-testid="payroll-remittances">
        <summary className={SUMMARY}>Tax paid over</summary>
        {payrollTax.remittances.length === 0 ? (
          <p className="mt-2 text-[13px] text-stone">No payment of payroll tax was identified on the bank statements.</p>
        ) : (
          <RuledList>
            {payrollTax.remittances.map((r, index) => (
              <div key={`${index}-${r.source}`} className="flex flex-wrap items-baseline justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-[14px] text-ink">{r.dateText} · “{r.words}”</p>
                  {r.basis && <p className="text-[12px] text-stone">{r.basis}</p>}
                  {tick(r.source)}
                </div>
                <div className="text-right">
                  <div className="font-mono text-[14px] text-ink [font-variant-numeric:tabular-nums]">{r.amountText}</div>
                  <div className="text-[12px]"><StateText tone={markerTone(r.tone)}>{r.statusLabel}</StateText></div>
                </div>
              </div>
            ))}
          </RuledList>
        )}
      </details>

      {payrollTax.rollForward.rows.length > 0 && (
        <details className="mt-4" data-testid="payroll-roll-forward">
          <summary className={SUMMARY}>Payroll tax owed, month by month</summary>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead><tr><th className={TH}>Month</th><th className={`${TH} ${NUM}`}>Owed at the start</th><th className={`${TH} ${NUM}`}>Withheld</th><th className={`${TH} ${NUM}`}>Paid over</th><th className={`${TH} ${NUM}`}>Owed at the end</th></tr></thead>
              <tbody>
                {payrollTax.rollForward.rows.map((r) => (
                  <tr key={r.label}>
                    <td className={TD}>{r.label}</td><td className={`${TD} ${NUM}`}>{r.openingText ?? "—"}</td><td className={`${TD} ${NUM}`}>{r.withheldText}</td>
                    <td className={`${TD} ${NUM}`}>{r.paidText}</td><td className={`${TD} ${NUM}`}>{r.closingText ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {payrollTax.rollForward.note && <p className="mt-2 text-[13px] text-stone">{payrollTax.rollForward.note}</p>}
        </details>
      )}

      <div className="mt-5" data-testid="payroll-control">
        <Label>The books</Label>
        <p className="mt-1.5 text-[14px] text-ink"><StateText tone={markerTone(payrollTax.controlAccount.tone)}>{payrollTax.controlAccount.stateLabel}</StateText>. {payrollTax.controlAccount.sentence}</p>
      </div>

      {payrollTax.notes.length > 0 && (
        <RuledList className="mt-4">
          {payrollTax.notes.map((note) => <p key={note} className="py-2 text-[13px] text-stone">{note}</p>)}
        </RuledList>
      )}
    </Section>
  );
}
